import {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useMemo,
  useReducer,
} from 'react';
import { AlertService, OrderService, RequestForOffersService } from 'api';
import { PollAlertToast } from 'components/PollAlertToast';
import { useAuth } from 'contexts/authContext';
import { t } from 'i18next';
import { appToast } from 'utils/toast.utils';
import {
  AlertsContextAction,
  AlertsContextActionType,
  AlertsContextDispatch,
  AlertsContextState,
} from './alertsContext.types';

const initialState: AlertsContextState = {
  alertsCount: 0,
  ordersCount: 0,
  requestForOffersCount: 0,
  alertsInbox: [],
};

const alertsReducer = (
  state: AlertsContextState,
  action: AlertsContextAction,
) => {
  switch (action.type) {
    case AlertsContextActionType.SET_NEW_ALERTS:
      return {
        ...state,
        alertsCount: action.alertsCount,
        ordersCount: action.ordersCount,
        requestForOffersCount: action.requestForOffersCount,
        alertsInbox: action.alertsInbox,
      };
    case AlertsContextActionType.SET_REQUEST_FOR_OFFERS_COUNT:
      return {
        ...state,
        requestForOffersCount: action.requestForOffersCount,
      };
    case AlertsContextActionType.SET_ALERTS_COUNT:
      return {
        ...state,
        alertsCount: action.alertsCount,
      };
    case AlertsContextActionType.SET_ALERTS_INBOX:
      return {
        ...state,
        alertsInbox: action.alertsInbox,
      };
    default: {
      throw new Error(`Unhandled action type`);
    }
  }
};

const alertsContext = createContext<
  { state: AlertsContextState; dispatch: AlertsContextDispatch } | undefined
>(undefined);

export const AlertsContextProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  const [state, dispatch] = useReducer(alertsReducer, initialState);
  const { user } = useAuth();

  const value = useMemo(() => ({ state, dispatch }), [state, dispatch]);

  useEffect(() => {
    let interval: NodeJS.Timer | undefined;
    if (user) {
      pollAlerts(dispatch);
      interval = setInterval(() => pollAlerts(dispatch), 60000);
    }
    return () => {
      clearInterval(interval);
    };
  }, [user]);

  return (
    <alertsContext.Provider value={value}>{children}</alertsContext.Provider>
  );
};

export const useAlerts = () => {
  const context = useContext(alertsContext);

  if (context === undefined) {
    throw new Error('useAlerts must be used within a AlertsProvider');
  }

  return context;
};

export const getAlertsCountState = async (dispatch: AlertsContextDispatch) => {
  const response = await AlertService.getAlertsCount();
  dispatch({
    type: AlertsContextActionType.SET_ALERTS_COUNT,
    alertsCount: response.data.result,
  });
};

export const getRequestsForOffersCountState = async (
  dispatch: AlertsContextDispatch,
) => {
  const response = await RequestForOffersService.getRequestsForOffersCount();
  dispatch({
    type: AlertsContextActionType.SET_REQUEST_FOR_OFFERS_COUNT,
    requestForOffersCount: response.data.result,
  });
};

export const getOrdersCountState = async (dispatch: AlertsContextDispatch) => {
  const response = await OrderService.getOrdersCount();
  dispatch({
    type: AlertsContextActionType.SET_ORDERS_COUNT,
    ordersCount: response.data.result,
  });
};

export const getAllAlertsState = async (dispatch: AlertsContextDispatch) => {
  try {
    const [alertsRes, requestForOffersRes, ordersRes, alertsInboxRes] =
      await Promise.all([
        AlertService.getAlertsCount(),
        RequestForOffersService.getRequestsForOffersCount(),
        OrderService.getOrdersCount(),
        AlertService.getAlertsInbox(),
      ]);
    dispatch({
      type: AlertsContextActionType.SET_NEW_ALERTS,
      alertsCount: alertsRes.data.result,
      requestForOffersCount: requestForOffersRes.data.result,
      ordersCount: ordersRes.data.result,
      alertsInbox: alertsInboxRes.data.result,
    });
  } catch (e) {}
};

export const getAlertsInboxState = async (dispatch: AlertsContextDispatch) => {
  const response = await AlertService.getAlertsInbox();
  dispatch({
    type: AlertsContextActionType.SET_ALERTS_INBOX,
    alertsInbox: response.data.result,
  });
};

export const pollAlerts = async (dispatch: AlertsContextDispatch) => {
  try {
    const response = await AlertService.getAlertsPoll();

    if (response.data.result.length) {
      getAllAlertsState(dispatch);
      response.data.result
        .filter((alert) => !alert.message.toLowerCase().includes('saldeo'))
        .forEach((alert) => {
          switch (alert.displayType) {
            case 'INFO':
              return appToast.info(
                t('message.newMessage', { ns: 'misc' }),
                <PollAlertToast alert={alert} />,
              );
            case 'SUCCESS':
              return appToast.success(
                t('message.newMessage', { ns: 'misc' }),
                <PollAlertToast alert={alert} />,
              );
            case 'WARNING':
              return appToast.warning(
                t('message.newMessage', { ns: 'misc' }),
                <PollAlertToast alert={alert} />,
              );
            case 'ERROR':
              return appToast.error(
                t('message.newMessage', { ns: 'misc' }),
                <PollAlertToast alert={alert} />,
              );
            default:
          }
        });
    }
  } catch (e) {}
};
